import {type ComponentType} from 'react'
import {View} from 'react-native'
import {msg} from '@lingui/core/macro'
import {useLingui} from '@lingui/react'

import {atoms as a, useTheme} from '#/alf'
import {type Props as IconProps} from '#/components/icons/common'
import {Hashtag_Stroke2_Corner0_Rounded as HashtagIcon} from '#/components/icons/Hashtag'
import {MagnifyingGlass_Stroke2_Corner0_Rounded as SearchIcon} from '#/components/icons/MagnifyingGlass'
import {Newspaper_Stroke2_Corner2_Rounded as NewspaperIcon} from '#/components/icons/Newspaper'
import {Trending2_Stroke2_Corner2_Rounded as TrendingIcon} from '#/components/icons/Trending'
import {Text} from '#/components/Typography'
import {useSemanticTokens} from '#/wsocial/service/design-system'
import {highSignalKeys, type Trend, type TrendSignalKey} from '../data/trends'

type IconComponent = ComponentType<IconProps>

/**
 * The signal/velocity badge row on the #1 Top trends hero. Replaces the
 * posts-per-hour pills. Renders (in order): the velocity pill ("+130% past
 * hour"), then a badge for each ranking signal at `level: 'high'` —
 * "High search volume", "Trending on social media", "Trending in the news".
 * Med/low signals and a `null` velocity render nothing; when neither is present
 * the whole row is omitted (the caller falls back to the legacy metric pills).
 */
export function TrendHeroBadges({trend}: {trend: Trend}) {
  const {_} = useLingui()

  const velocity = trend.velocity ?? undefined
  const signalKeys = highSignalKeys(trend.signals)
  if (!velocity && signalKeys.length === 0) return null

  const signalMeta: Record<
    TrendSignalKey,
    {Icon: IconComponent; label: string}
  > = {
    search: {Icon: SearchIcon, label: _(msg`High search volume`)},
    social: {Icon: HashtagIcon, label: _(msg`Trending on social media`)},
    media: {Icon: NewspaperIcon, label: _(msg`Trending in the news`)},
  }

  return (
    <View style={[a.flex_row, a.flex_wrap, a.gap_xs]}>
      {velocity && (
        <Badge
          Icon={TrendingIcon}
          value={formatSignedPercent(velocity.growthPercent)}
          label={
            velocity.sinceHours === 1
              ? _(msg`past hour`)
              : _(msg`past ${velocity.sinceHours} hours`)
          }
        />
      )}
      {signalKeys.map(key => (
        <Badge
          key={key}
          Icon={signalMeta[key].Icon}
          label={signalMeta[key].label}
        />
      ))}
    </View>
  )
}

function Badge({
  Icon,
  value,
  label,
}: {
  Icon: IconComponent
  value?: string
  label: string
}) {
  const t = useTheme()
  const ss = useSemanticTokens()
  return (
    <View
      style={[
        a.flex_row,
        a.align_center,
        a.gap_xs,
        a.rounded_full,
        {height: 32, paddingHorizontal: 11, backgroundColor: ss.bg.contrast_25},
      ]}>
      <Icon size="xs" fill={t.atoms.text_contrast_medium.color} />
      <Text style={[a.text_xs, a.leading_snug]} numberOfLines={1}>
        {value && (
          <Text style={[a.text_xs, a.font_bold, t.atoms.text]}>{value} </Text>
        )}
        <Text style={[a.text_xs, a.font_medium, t.atoms.text_contrast_medium]}>
          {label}
        </Text>
      </Text>
    </View>
  )
}

function formatSignedPercent(n: number): string {
  return n > 0 ? `+${n}%` : `${n}%`
}
